import { action } from "typesafe-actions";
import { AnyAction } from "redux";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../../initialStoreState";
import { api } from "../../../api/api";
import { IExternalLeadStatus, IExternalLeadMasterStatus } from "./external-lead-status.types";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../../messages/messagesActions";

import { ISearchQueryParams } from "../../store.types";
import { getSearchQueryParams } from "../../store.utils";

// Action Types
export const FETCH_EXTERNAL_STATUS_LIST_PROGRESS = "FETCH_EXTERNAL_STATUS_LIST_PROGRESS";
export const FETCH_EXTERNAL_STATUS_LIST_SUCCESS = "FETCH_EXTERNAL_STATUS_LIST_SUCCESS";
export const FETCH_EXTERNAL_STATUS_LIST_FAILED = "FETCH_EXTERNAL_STATUS_LIST_FAILED";

export const FETCH_EXTERNAL_STATUS_PROGRESS = "FETCH_EXTERNAL_STATUS_PROGRESS";
export const FETCH_EXTERNAL_STATUS_SUCCESS = "FETCH_EXTERNAL_STATUS_SUCCESS";
export const FETCH_EXTERNAL_STATUS_FAILED = "FETCH_EXTERNAL_STATUS_FAILED";

// Action Creators
export const fetchExternalStatusListProgress = () => action(FETCH_EXTERNAL_STATUS_LIST_PROGRESS);
export const fetchExternalStatusListSuccess = (data: IExternalLeadStatus[]) =>
  action(FETCH_EXTERNAL_STATUS_LIST_SUCCESS, { data });
export const fetchExternalStatusListFailed = () => action(FETCH_EXTERNAL_STATUS_LIST_FAILED);

export const fetchExternalStatusProgress = () => action(FETCH_EXTERNAL_STATUS_PROGRESS);
export const fetchExternalStatusSuccess = (data: IExternalLeadStatus) =>
  action(FETCH_EXTERNAL_STATUS_SUCCESS, { data });
export const fetchExternalStatusFailed = () => action(FETCH_EXTERNAL_STATUS_FAILED);

// Async Actions
export const fetchExternalStatusListAsync =
  (queryParams: ISearchQueryParams
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
      try {
        const { signal } = queryParams;
        const searchQuery = getSearchQueryParams(queryParams);
        dispatch(fetchExternalStatusListProgress());
        const res = await api.get(`/dataManagement/get-drive-external-status`, {
          params: searchQuery,
          signal
        });
        const data: IExternalLeadStatus[] = res.data.data;

        dispatch(fetchExternalStatusListSuccess(data));
      } catch (err: any) {
        dispatch(fetchExternalStatusListFailed());
        dispatch(
          showMessage({
            type: "error",
            message: err.response?.data?.message || "Failed to fetch external status list",
            displayAs: "snackbar",
          })
        );
      }
    };

export const fetchExternalStatusAsync =
  (statusUuid: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
      try {
        dispatch(fetchExternalStatusProgress());
        dispatch(saveLoaderProgress());
        const res = await api.get(`/dataManagement/get-drive-external-status?status_uuid=${statusUuid}`);
        const data = res.data.data;
        if (data.length > 0) {
          dispatch(fetchExternalStatusSuccess(data[0]));
        } else {
          dispatch(fetchExternalStatusFailed());
        }
      } catch (err: any) {
        dispatch(fetchExternalStatusFailed());
        dispatch(
          showMessage({
            type: "error",
            message: err.response?.data?.message || "Failed to fetch external status",
            displayAs: "snackbar",
          })
        );
      } finally {
        dispatch(saveLoaderCompleted());
      }
    };

export const upsertExternalStatusAsync =
  (
    status: IExternalLeadStatus,
    onCallback: (isSuccess?: boolean, data?: IExternalLeadStatus) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
      dispatch(saveLoaderProgress());
      try {
        const {
          created_by_uuid,
          created_by_name,
          modified_by_uuid,
          modified_by_name,
          ...rest
        } = status;
        const res = await api.post("/dataManagement/upsert-drive-external-status", {
          ...rest,
        });
        dispatch(
          showMessage({
            type: "success",
            message: res.data.message,
            displayAs: "snackbar",
          })
        );
        const data = res.data.data;
        onCallback(true, data);
      } catch (err: any) {
        onCallback(false);
        dispatch(
          showMessage({
            type: "error",
            message: err.response?.data?.message || "Failed to upsert external status",
            displayAs: "snackbar",
          })
        );
      } finally {
        dispatch(saveLoaderCompleted());
      }
    };

// Master External Status Action Types
export const FETCH_MASTER_EXTERNAL_STATUS_LIST_PROGRESS =
  "FETCH_MASTER_EXTERNAL_STATUS_LIST_PROGRESS";
export const FETCH_MASTER_EXTERNAL_STATUS_LIST_SUCCESS =
  "FETCH_MASTER_EXTERNAL_STATUS_LIST_SUCCESS";
export const FETCH_MASTER_EXTERNAL_STATUS_LIST_FAILED =
  "FETCH_MASTER_EXTERNAL_STATUS_LIST_FAILED";

export const FETCH_MASTER_EXTERNAL_STATUS_PROGRESS = "FETCH_MASTER_EXTERNAL_STATUS_PROGRESS";
export const FETCH_MASTER_EXTERNAL_STATUS_SUCCESS = "FETCH_MASTER_EXTERNAL_STATUS_SUCCESS";
export const FETCH_MASTER_EXTERNAL_STATUS_FAILED = "FETCH_MASTER_EXTERNAL_STATUS_FAILED";

// Action Creators
export const fetchMasterExternalStatusListProgress = () =>
  action(FETCH_MASTER_EXTERNAL_STATUS_LIST_PROGRESS);
export const fetchMasterExternalStatusListSuccess = (data: IExternalLeadMasterStatus[]) =>
  action(FETCH_MASTER_EXTERNAL_STATUS_LIST_SUCCESS, { data });
export const fetchMasterExternalStatusListFailed = () =>
  action(FETCH_MASTER_EXTERNAL_STATUS_LIST_FAILED);

export const fetchMasterExternalStatusProgress = () =>
  action(FETCH_MASTER_EXTERNAL_STATUS_PROGRESS);
export const fetchMasterExternalStatusSuccess = (data: IExternalLeadMasterStatus) =>
  action(FETCH_MASTER_EXTERNAL_STATUS_SUCCESS, { data });
export const fetchMasterExternalStatusFailed = () => action(FETCH_MASTER_EXTERNAL_STATUS_FAILED);

// Async Actions
export const fetchMasterExternalStatusListAsync =
  (queryParams: ISearchQueryParams
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
      try {
        const { signal } = queryParams;
        const searchQuery = getSearchQueryParams(queryParams);
        dispatch(fetchMasterExternalStatusListProgress());
        const res = await api.get(`/siteConfiguration/get-master-external-status`, {
          params: searchQuery,
          signal
        });
        const data: IExternalLeadMasterStatus[] = res.data.data;

        dispatch(fetchMasterExternalStatusListSuccess(data));
      } catch (err: any) {
        dispatch(fetchMasterExternalStatusListFailed());
        dispatch(
          showMessage({
            type: "error",
            message: err.response?.data?.message || "Failed to fetch master external status list",
            displayAs: "snackbar",
          })
        );
      }
    };

export const fetchMasterExternalStatusAsync =
  (statusUuid: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
      try {
        dispatch(fetchMasterExternalStatusProgress());
        dispatch(saveLoaderProgress());
        const res = await api.get(
          `/siteConfiguration/get-master-external-status?master_status_uuid=${statusUuid}`
        );
        const data = res.data.data;
        if (data.length > 0) {
          dispatch(fetchMasterExternalStatusSuccess(data[0]));
        } else {
          dispatch(fetchMasterExternalStatusFailed());
        }
      } catch (err: any) {
        dispatch(fetchMasterExternalStatusFailed());
        dispatch(
          showMessage({
            type: "error",
            message: err.response?.data?.message || "Failed to fetch master external status",
            displayAs: "snackbar",
          })
        );
      } finally {
        dispatch(saveLoaderCompleted());
      }
    };

export const upsertMasterExternalStatusAsync =
  (
    status: IExternalLeadMasterStatus,
    onCallback: (isSuccess?: boolean, data?: IExternalLeadMasterStatus) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
      dispatch(saveLoaderProgress());
      try {
        const {
          created_by_uuid,
          created_by_name,
          modified_by_uuid,
          modified_by_name,
          ...rest
        } = status;
        const res = await api.post("/siteConfiguration/upsert-master-external-status", {
          ...rest,
        });
        dispatch(
          showMessage({
            type: "success",
            message: res.data.message,
            displayAs: "snackbar",
          })
        );
        const data = res.data.data;
        onCallback(true, data);
      } catch (err: any) {
        onCallback(false);
        dispatch(
          showMessage({
            type: "error",
            message: err.response?.data?.message || "Failed to upsert master external status",
            displayAs: "snackbar",
          })
        );
      } finally {
        dispatch(saveLoaderCompleted());
      }
    };

export const CLEAR_EXTERNAL_STATUS_STATE = "CLEAR_EXTERNAL_STATUS_STATE";
export const CLEAR_EXTERNAL_STATUS = "CLEAR_EXTERNAL_STATUS";
export const CLEAR_MASTER_EXTERNAL_STATUS_STATE = "CLEAR_MASTER_EXTERNAL_STATUS_STATE";
export const CLEAR_MASTER_EXTERNAL_STATUS = "CLEAR_MASTER_EXTERNAL_STATUS";

export const clearExternalStatusState = () => action(CLEAR_EXTERNAL_STATUS_STATE);
export const clearExternalStatus = () => action(CLEAR_EXTERNAL_STATUS);
export const clearMasterExternalStatusState = () => action(CLEAR_MASTER_EXTERNAL_STATUS_STATE);
export const clearMasterExternalStatus = () => action(CLEAR_MASTER_EXTERNAL_STATUS);

// Outlet External Status Lifecycle (drive status category list)
export const FETCH_OUTLET_EXTERNAL_STATUS_LIFECYCLE_PROGRESS =
  "FETCH_OUTLET_EXTERNAL_STATUS_LIFECYCLE_PROGRESS";
export const FETCH_OUTLET_EXTERNAL_STATUS_LIFECYCLE_SUCCESS =
  "FETCH_OUTLET_EXTERNAL_STATUS_LIFECYCLE_SUCCESS";
export const FETCH_OUTLET_EXTERNAL_STATUS_LIFECYCLE_FAILED =
  "FETCH_OUTLET_EXTERNAL_STATUS_LIFECYCLE_FAILED";

export const fetchOutletExternalStatusLifecycleProgress = () =>
  action(FETCH_OUTLET_EXTERNAL_STATUS_LIFECYCLE_PROGRESS);
export const fetchOutletExternalStatusLifecycleSuccess = (data: any[]) =>
  action(FETCH_OUTLET_EXTERNAL_STATUS_LIFECYCLE_SUCCESS, { data });
export const fetchOutletExternalStatusLifecycleFailed = (errorMessage: string) =>
  action(FETCH_OUTLET_EXTERNAL_STATUS_LIFECYCLE_FAILED, { errorMessage });

export const fetchOutletExternalStatusLifecycleAsync =
  (companyOutletUuid: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
      try {
        dispatch(fetchOutletExternalStatusLifecycleProgress());

        const res = await api.get(
          `/dataManagement/get-drive-external-status-list?company_outlet_uuid=${companyOutletUuid}`,
        );

        const data = res.data.data;
        dispatch(fetchOutletExternalStatusLifecycleSuccess(data));
      } catch (err: any) {
        dispatch(fetchOutletExternalStatusLifecycleFailed("Something went wrong!"));
        dispatch(
          showMessage({
            type: "error",
            message: err.response?.data?.message || "Something went wrong!",
            displayAs: "snackbar",
          }),
        );
      }
    };
